import { c as _c } from "react/compiler-runtime";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Check, Clock, Loader2, Save, Trash2, User } from "lucide-react";
import { useFeatureDetail } from "@/lib/hooks/useFeatureDetail";
import { useIsAdmin } from "@/lib/hooks/useIsAdmin";
import { DOOR_TYPES, DOOR_WIDTHS, FEATURE_LEVELS, formatDistanceMeters, PARKING_PAYMENT, PARKING_SPACE_TYPES, SURFACE_TYPES } from "@/lib/utils/accessibilityLabels";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const BOOL_OPTIONS = [{
  value: "true",
  label: "Ja"
}, {
  value: "false",
  label: "Nej"
}];
export function FeatureDetailView() {
  const $ = _c(106);
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const {
    feature,
    isLoading,
    featureTypes,
    featureTypeId,
    setFeatureTypeId,
    form,
    setFormField: set,
    saveMutation,
    approveMutation,
    rejectMutation,
    deleteMutation,
    deleteMediaMutation
  } = useFeatureDetail();
  if (!isAdmin) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = /*#__PURE__*/_jsx("div", {
        className: "container mx-auto flex min-h-[60vh] items-center justify-center px-4",
        children: /*#__PURE__*/_jsx("p", {
          className: "text-gray-500",
          children: "Du har inte beh\xF6righet."
        })
      });
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (isLoading) {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = /*#__PURE__*/_jsx("div", {
        className: "container mx-auto flex min-h-[60vh] items-center justify-center px-4",
        children: /*#__PURE__*/_jsx(Loader2, {
          className: "h-6 w-6 animate-spin text-gray-400"
        })
      });
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (!feature) {
    let t0;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = /*#__PURE__*/_jsx("div", {
        className: "container mx-auto flex min-h-[60vh] items-center justify-center px-4",
        children: /*#__PURE__*/_jsx("p", {
          className: "text-gray-500",
          children: "Hittades inte."
        })
      });
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  let t0;
  if ($[3] !== navigate) {
    t0 = () => navigate({
      to: "/admin/granska"
    });
    $[3] = navigate;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /*#__PURE__*/_jsx(ArrowLeft, {
      className: "mr-1 h-4 w-4"
    });
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t0) {
    t2 = /*#__PURE__*/_jsxs(Button, {
      variant: "ghost",
      size: "sm",
      onClick: t0,
      children: [t1, "Tillbaka"]
    });
    $[6] = t0;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== feature.featureType.name || $[9] !== feature.place.name) {
    t3 = /*#__PURE__*/_jsxs("h1", {
      className: "text-xl font-bold text-gray-900",
      children: [feature.featureType.name, " \u2014 ", feature.place.name]
    });
    $[8] = feature.featureType.name;
    $[9] = feature.place.name;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = feature.status === "approved" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700";
  let t5;
  if ($[11] !== t4) {
    t5 = cn("rounded-full px-2 py-0.5 text-xs font-medium", t4);
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = feature.status === "approved" ? "Verifierad" : "V\xE4ntar";
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = /*#__PURE__*/_jsx("span", {
      className: t5,
      children: t6
    });
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t2 || $[17] !== t3 || $[18] !== t7) {
    t8 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-3",
      children: [t2, t3, t7]
    });
    $[16] = t2;
    $[17] = t3;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== saveMutation) {
    t9 = () => saveMutation.mutate();
    $[20] = saveMutation;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== saveMutation.isPending) {
    t10 = saveMutation.isPending ? /*#__PURE__*/_jsx(Loader2, {
      className: "h-3.5 w-3.5 animate-spin"
    }) : /*#__PURE__*/_jsx(Save, {
      className: "h-3.5 w-3.5"
    });
    $[22] = saveMutation.isPending;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== saveMutation.isPending || $[25] !== t10 || $[26] !== t9) {
    t11 = /*#__PURE__*/_jsxs(Button, {
      size: "sm",
      onClick: t9,
      disabled: saveMutation.isPending,
      className: "gap-1.5 bg-brand hover:bg-brand/90",
      children: [t10, "Spara"]
    });
    $[24] = saveMutation.isPending;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== approveMutation || $[29] !== feature.status || $[30] !== rejectMutation) {
    t12 = feature.status === "pending" && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Button, {
        size: "sm",
        onClick: () => approveMutation.mutate(),
        disabled: approveMutation.isPending,
        className: "gap-1.5 bg-green-600 hover:bg-green-700",
        children: [/*#__PURE__*/_jsx(Check, {
          className: "h-3.5 w-3.5"
        }), "Godk\xE4nn"]
      }), /*#__PURE__*/_jsxs(Button, {
        size: "sm",
        variant: "destructive",
        onClick: () => {
          if (confirm("Avvisa och ta bort?")) {
            rejectMutation.mutate();
          }
        },
        disabled: rejectMutation.isPending,
        className: "gap-1.5",
        children: [/*#__PURE__*/_jsx(Trash2, {
          className: "h-3.5 w-3.5"
        }), "Avvisa"]
      })]
    });
    $[28] = approveMutation;
    $[29] = feature.status;
    $[30] = rejectMutation;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== deleteMutation) {
    t13 = () => {
      if (confirm("Radera denna feature permanent?")) {
        deleteMutation.mutate();
      }
    };
    $[32] = deleteMutation;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== deleteMutation.isPending) {
    t14 = deleteMutation.isPending ? /*#__PURE__*/_jsx(Loader2, {
      className: "h-3.5 w-3.5 animate-spin"
    }) : /*#__PURE__*/_jsx(Trash2, {
      className: "h-3.5 w-3.5"
    });
    $[34] = deleteMutation.isPending;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== deleteMutation.isPending || $[37] !== t13 || $[38] !== t14) {
    t15 = /*#__PURE__*/_jsxs(Button, {
      size: "sm",
      variant: "destructive",
      onClick: t13,
      disabled: deleteMutation.isPending,
      className: "gap-1.5",
      children: [t14, "Radera"]
    });
    $[36] = deleteMutation.isPending;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t11 || $[41] !== t12 || $[42] !== t15) {
    t16 = /*#__PURE__*/_jsxs("div", {
      className: "flex gap-2",
      children: [t11, t12, t15]
    });
    $[40] = t11;
    $[41] = t12;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t16 || $[45] !== t8) {
    t17 = /*#__PURE__*/_jsxs("div", {
      className: "mb-6 flex items-center justify-between",
      children: [t8, t16]
    });
    $[44] = t16;
    $[45] = t8;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== deleteMediaMutation || $[48] !== feature.featureType.name || $[49] !== feature.media) {
    t18 = feature.media.length > 0 ? /*#__PURE__*/_jsx("div", {
      className: "space-y-2",
      children: feature.media.map(m => /*#__PURE__*/_jsxs("div", {
        className: "group relative",
        children: [/*#__PURE__*/_jsx("img", {
          src: m.url,
          alt: feature.featureType.name,
          className: "w-full rounded-lg object-cover"
        }), /*#__PURE__*/_jsx("button", {
          type: "button",
          onClick: () => {
            if (confirm("Ta bort denna bild?")) {
              deleteMediaMutation.mutate(m.id);
            }
          },
          className: "absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white opacity-0 transition-opacity group-hover:opacity-100",
          children: /*#__PURE__*/_jsx(Trash2, {
            className: "h-4 w-4"
          })
        })]
      }, m.id))
    }) : /*#__PURE__*/_jsx("div", {
      className: "flex aspect-[4/3] items-center justify-center rounded-lg bg-gray-100",
      children: /*#__PURE__*/_jsx("p", {
        className: "text-sm text-gray-400",
        children: "Ingen bild"
      })
    });
    $[47] = deleteMediaMutation;
    $[48] = feature.featureType.name;
    $[49] = feature.media;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = /*#__PURE__*/_jsx("span", {
      className: "text-gray-500",
      children: "ID"
    });
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== feature.id) {
    t20 = /*#__PURE__*/_jsxs("div", {
      className: "flex justify-between",
      children: [t19, /*#__PURE__*/_jsx("span", {
        className: "font-medium",
        children: feature.id
      })]
    });
    $[52] = feature.id;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = /*#__PURE__*/_jsx("span", {
      className: "text-gray-500",
      children: "Plats"
    });
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== feature.place.id) {
    t22 = {
      plats: feature.place.id
    };
    $[55] = feature.place.id;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== feature.place.name || $[58] !== t22) {
    t23 = /*#__PURE__*/_jsxs("div", {
      className: "flex justify-between",
      children: [t21, /*#__PURE__*/_jsxs(Link, {
        to: "/karta",
        search: t22,
        className: "font-medium text-brand hover:underline",
        children: [feature.place.name, " \u2192"]
      })]
    });
    $[57] = feature.place.name;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = /*#__PURE__*/_jsx("span", {
      className: "text-gray-500",
      children: "Inskickad av"
    });
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = /*#__PURE__*/_jsx(User, {
      className: "h-3 w-3"
    });
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  const t26 = feature.creator?.name ?? "Ok\xE4nd";
  let t27;
  if ($[62] !== t26) {
    t27 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center justify-between",
      children: [t24, /*#__PURE__*/_jsxs("span", {
        className: "flex items-center gap-1 font-medium",
        children: [t25, t26]
      })]
    });
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = /*#__PURE__*/_jsx("span", {
      className: "text-gray-500",
      children: "Datum"
    });
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = /*#__PURE__*/_jsx(Clock, {
      className: "h-3 w-3"
    });
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== feature.createdAt) {
    t30 = new Date(feature.createdAt).toLocaleString("sv-SE");
    $[66] = feature.createdAt;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== t30) {
    t31 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center justify-between",
      children: [t28, /*#__PURE__*/_jsxs("span", {
        className: "flex items-center gap-1 font-medium",
        children: [t29, t30]
      })]
    });
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== feature.assessment) {
    t32 = feature.assessment && /*#__PURE__*/_jsxs("div", {
      className: "flex justify-between",
      children: [/*#__PURE__*/_jsx("span", {
        className: "text-gray-500",
        children: "K\xE4lla"
      }), /*#__PURE__*/_jsx("span", {
        className: "font-medium",
        children: feature.assessment.source === "ai" ? "AI" : feature.assessment.source === "professional" ? "Admin" : "Anv\xE4ndare"
      })]
    });
    $[70] = feature.assessment;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = /*#__PURE__*/_jsx("span", {
      className: "text-gray-500",
      children: "Redigera plats"
    });
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  const t34 = String(feature.place.id);
  let t35;
  if ($[73] !== t34) {
    t35 = /*#__PURE__*/_jsxs("div", {
      className: "flex justify-between",
      children: [t33, /*#__PURE__*/_jsx(Link, {
        to: "/admin/platser/$id",
        params: {
          id: t34
        },
        className: "font-medium text-brand hover:underline",
        children: "\xD6ppna \u2192"
      })]
    });
    $[73] = t34;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  let t36;
  if ($[75] !== t20 || $[76] !== t23 || $[77] !== t27 || $[78] !== t31 || $[79] !== t32 || $[80] !== t35) {
    t36 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-2 rounded-lg border bg-gray-50 p-4 text-sm",
      children: [t20, t23, t27, t31, t32, t35]
    });
    $[75] = t20;
    $[76] = t23;
    $[77] = t27;
    $[78] = t31;
    $[79] = t32;
    $[80] = t35;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== t18 || $[83] !== t36) {
    t37 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-4",
      children: [t18, t36]
    });
    $[82] = t18;
    $[83] = t36;
    $[84] = t37;
  } else {
    t37 = $[84];
  }
  let t38;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = /*#__PURE__*/_jsx("h2", {
      className: "text-sm font-medium uppercase tracking-wider text-gray-400",
      children: "Bed\xF6mning"
    });
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  let t39;
  if ($[86] !== featureTypes) {
    t39 = featureTypes.map(_temp);
    $[86] = featureTypes;
    $[87] = t39;
  } else {
    t39 = $[87];
  }
  let t40;
  if ($[88] !== featureTypeId || $[89] !== setFeatureTypeId || $[90] !== t39) {
    t40 = /*#__PURE__*/_jsx(Field, {
      label: "Typ",
      children: /*#__PURE__*/_jsx(MiniSelect, {
        value: featureTypeId,
        onChange: setFeatureTypeId,
        options: t39
      })
    });
    $[88] = featureTypeId;
    $[89] = setFeatureTypeId;
    $[90] = t39;
    $[91] = t40;
  } else {
    t40 = $[91];
  }
  let t41;
  if ($[92] !== feature.distanceMeters || $[93] !== feature.featureType.name || $[94] !== form || $[95] !== set) {
    t41 = feature.featureType.name === "Parkering" ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Field, {
        label: "Typ av ruta",
        children: /*#__PURE__*/_jsx(MiniSelect, {
          value: form.parkingSpaceType,
          onChange: v => set("parkingSpaceType", v),
          options: Object.entries(PARKING_SPACE_TYPES).map(_temp2)
        })
      }), /*#__PURE__*/_jsx(Field, {
        label: "Avst\xE5nd till entr\xE9",
        children: /*#__PURE__*/_jsx("span", {
          className: "text-sm font-medium text-gray-900",
          children: formatDistanceMeters(feature.distanceMeters)
        })
      }), /*#__PURE__*/_jsx(Field, {
        label: "Skylt (rullstol)",
        children: /*#__PURE__*/_jsx(MiniSelect, {
          value: String(form.hasWheelchairSign),
          onChange: v_1 => set("hasWheelchairSign", v_1 === "true"),
          options: BOOL_OPTIONS
        })
      }), /*#__PURE__*/_jsx(Field, {
        label: "Markm\xE5lning",
        children: /*#__PURE__*/_jsx(MiniSelect, {
          value: String(form.hasGroundMarkings),
          onChange: v_2 => set("hasGroundMarkings", v_2 === "true"),
          options: BOOL_OPTIONS
        })
      }), /*#__PURE__*/_jsx(Field, {
        label: "Neds\xE4nkt kantsten",
        children: /*#__PURE__*/_jsx(MiniSelect, {
          value: String(form.hasKerbCut),
          onChange: v_3 => set("hasKerbCut", v_3 === "true"),
          options: BOOL_OPTIONS
        })
      }), /*#__PURE__*/_jsx(Field, {
        label: "Tak \xF6ver",
        children: /*#__PURE__*/_jsx(MiniSelect, {
          value: String(form.isCovered),
          onChange: v_4 => set("isCovered", v_4 === "true"),
          options: BOOL_OPTIONS
        })
      }), /*#__PURE__*/_jsx(Field, {
        label: "Avgift",
        children: /*#__PURE__*/_jsx(MiniSelect, {
          value: form.paymentType,
          onChange: v_5 => set("paymentType", v_5),
          options: Object.entries(PARKING_PAYMENT).map(_temp3)
        })
      }), /*#__PURE__*/_jsx(Field, {
        label: "Underlag",
        children: /*#__PURE__*/_jsx(MiniSelect, {
          value: form.surfaceType,
          onChange: v_7 => set("surfaceType", v_7),
          options: Object.entries(SURFACE_TYPES).map(_temp4)
        })
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx("label", {
          className: "text-sm text-gray-500",
          children: "Anteckningar"
        }), /*#__PURE__*/_jsx("textarea", {
          value: form.additionalNotes,
          onChange: e => set("additionalNotes", e.target.value),
          className: "mt-1 h-20 w-full resize-none rounded border px-3 py-2 text-sm"
        })]
      })]
    }) : /*#__PURE__*/_jsx(EntranceFields, {
      form: form,
      set: set
    });
    $[92] = feature.distanceMeters;
    $[93] = feature.featureType.name;
    $[94] = form;
    $[95] = set;
    $[96] = t41;
  } else {
    t41 = $[96];
  }
  let t42;
  if ($[97] !== t40 || $[98] !== t41) {
    t42 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-3",
      children: [t38, t40, t41]
    });
    $[97] = t40;
    $[98] = t41;
    $[99] = t42;
  } else {
    t42 = $[99];
  }
  let t43;
  if ($[100] !== t37 || $[101] !== t42) {
    t43 = /*#__PURE__*/_jsxs("div", {
      className: "grid gap-6 lg:grid-cols-2",
      children: [t37, t42]
    });
    $[100] = t37;
    $[101] = t42;
    $[102] = t43;
  } else {
    t43 = $[102];
  }
  let t44;
  if ($[103] !== t17 || $[104] !== t43) {
    t44 = /*#__PURE__*/_jsxs("div", {
      className: "container mx-auto max-w-4xl px-4 py-8",
      children: [t17, t43]
    });
    $[103] = t17;
    $[104] = t43;
    $[105] = t44;
  } else {
    t44 = $[105];
  }
  return t44;
}
function _temp4(t0) {
  const [k_1, v_8] = t0;
  return {
    value: k_1,
    label: v_8
  };
}
function _temp3(t0) {
  const [k_0, v_6] = t0;
  return {
    value: k_0,
    label: v_6
  };
}
function _temp2(t0) {
  const [k, v_0] = t0;
  return {
    value: k,
    label: v_0
  };
}
function _temp(t) {
  return {
    value: String(t.id),
    label: t.name
  };
}
function EntranceFields(t0) {
  const $ = _c(83);
  const {
    form,
    set
  } = t0;
  const t1 = form.doorType;
  let t2;
  if ($[0] !== set) {
    t2 = v => set("doorType", v);
    $[0] = set;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = Object.entries(DOOR_TYPES).map(_temp5);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== form.doorType || $[4] !== t2) {
    t4 = /*#__PURE__*/_jsx(Field, {
      label: "D\xF6rtyp",
      children: /*#__PURE__*/_jsx(MiniSelect, {
        value: t1,
        onChange: t2,
        options: t3
      })
    });
    $[3] = form.doorType;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = form.estimatedDoorWidth;
  let t6;
  if ($[6] !== set) {
    t6 = v_1 => set("estimatedDoorWidth", v_1);
    $[6] = set;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = Object.entries(DOOR_WIDTHS).map(_temp6);
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== form.estimatedDoorWidth || $[10] !== t6) {
    t8 = /*#__PURE__*/_jsx(Field, {
      label: "D\xF6rrbredd",
      children: /*#__PURE__*/_jsx(MiniSelect, {
        value: t5,
        onChange: t6,
        options: t7
      })
    });
    $[9] = form.estimatedDoorWidth;
    $[10] = t6;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = form.surfaceType;
  let t10;
  if ($[12] !== set) {
    t10 = v_3 => set("surfaceType", v_3);
    $[12] = set;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = Object.entries(SURFACE_TYPES).map(_temp7);
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== form.surfaceType || $[16] !== t10) {
    t12 = /*#__PURE__*/_jsx(Field, {
      label: "Markyta",
      children: /*#__PURE__*/_jsx(MiniSelect, {
        value: t9,
        onChange: t10,
        options: t11
      })
    });
    $[15] = form.surfaceType;
    $[16] = t10;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = form.entranceLevel;
  let t14;
  if ($[18] !== set) {
    t14 = v_5 => set("entranceLevel", v_5);
    $[18] = set;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = Object.entries(FEATURE_LEVELS).map(_temp8);
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== form.entranceLevel || $[22] !== t14) {
    t16 = /*#__PURE__*/_jsx(Field, {
      label: "Niv\xE5",
      children: /*#__PURE__*/_jsx(MiniSelect, {
        value: t13,
        onChange: t14,
        options: t15
      })
    });
    $[21] = form.entranceLevel;
    $[22] = t14;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const t17 = String(form.hasDoorThreshold);
  let t18;
  if ($[24] !== set) {
    t18 = v_7 => set("hasDoorThreshold", v_7 === "true");
    $[24] = set;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== t17 || $[27] !== t18) {
    t19 = /*#__PURE__*/_jsx(Field, {
      label: "Tr\xF6skel",
      children: /*#__PURE__*/_jsx(MiniSelect, {
        value: t17,
        onChange: t18,
        options: BOOL_OPTIONS
      })
    });
    $[26] = t17;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const t20 = String(form.hasSteps);
  let t21;
  if ($[29] !== set) {
    t21 = v_8 => set("hasSteps", v_8 === "true");
    $[29] = set;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== t20 || $[32] !== t21) {
    t22 = /*#__PURE__*/_jsx(Field, {
      label: "Trappsteg",
      children: /*#__PURE__*/_jsx(MiniSelect, {
        value: t20,
        onChange: t21,
        options: BOOL_OPTIONS
      })
    });
    $[31] = t20;
    $[32] = t21;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== form.hasSteps || $[35] !== form.stepCount || $[36] !== set) {
    t23 = form.hasSteps && /*#__PURE__*/_jsx(Field, {
      label: "Antal steg",
      children: /*#__PURE__*/_jsx("input", {
        type: "number",
        min: 0,
        value: form.stepCount ?? 0,
        onChange: e => set("stepCount", parseInt(e.target.value) || null),
        className: "h-8 w-20 rounded border px-2 text-sm"
      })
    });
    $[34] = form.hasSteps;
    $[35] = form.stepCount;
    $[36] = set;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  const t24 = String(form.hasRamp);
  let t25;
  if ($[38] !== set) {
    t25 = v_9 => set("hasRamp", v_9 === "true");
    $[38] = set;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== t24 || $[41] !== t25) {
    t26 = /*#__PURE__*/_jsx(Field, {
      label: "Ramp",
      children: /*#__PURE__*/_jsx(MiniSelect, {
        value: t24,
        onChange: t25,
        options: BOOL_OPTIONS
      })
    });
    $[40] = t24;
    $[41] = t25;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  const t27 = String(form.hasHandrails);
  let t28;
  if ($[43] !== set) {
    t28 = v_10 => set("hasHandrails", v_10 === "true");
    $[43] = set;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] !== t27 || $[46] !== t28) {
    t29 = /*#__PURE__*/_jsx(Field, {
      label: "Handledare",
      children: /*#__PURE__*/_jsx(MiniSelect, {
        value: t27,
        onChange: t28,
        options: BOOL_OPTIONS
      })
    });
    $[45] = t27;
    $[46] = t28;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  const t30 = String(form.hasAutomaticDoorOpener);
  let t31;
  if ($[48] !== set) {
    t31 = v_11 => set("hasAutomaticDoorOpener", v_11 === "true");
    $[48] = set;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  let t32;
  if ($[50] !== t30 || $[51] !== t31) {
    t32 = /*#__PURE__*/_jsx(Field, {
      label: "Auto d\xF6rr\xF6ppnare",
      children: /*#__PURE__*/_jsx(MiniSelect, {
        value: t30,
        onChange: t31,
        options: BOOL_OPTIONS
      })
    });
    $[50] = t30;
    $[51] = t31;
    $[52] = t32;
  } else {
    t32 = $[52];
  }
  const t33 = String(form.hasContrastMarkings);
  let t34;
  if ($[53] !== set) {
    t34 = v_12 => set("hasContrastMarkings", v_12 === "true");
    $[53] = set;
    $[54] = t34;
  } else {
    t34 = $[54];
  }
  let t35;
  if ($[55] !== t33 || $[56] !== t34) {
    t35 = /*#__PURE__*/_jsx(Field, {
      label: "Kontrastmarkeringar",
      children: /*#__PURE__*/_jsx(MiniSelect, {
        value: t33,
        onChange: t34,
        options: BOOL_OPTIONS
      })
    });
    $[55] = t33;
    $[56] = t34;
    $[57] = t35;
  } else {
    t35 = $[57];
  }
  const t36 = String(form.hasIntercom);
  let t37;
  if ($[58] !== set) {
    t37 = v_13 => set("hasIntercom", v_13 === "true");
    $[58] = set;
    $[59] = t37;
  } else {
    t37 = $[59];
  }
  let t38;
  if ($[60] !== t36 || $[61] !== t37) {
    t38 = /*#__PURE__*/_jsx(Field, {
      label: "Porttelefon",
      children: /*#__PURE__*/_jsx(MiniSelect, {
        value: t36,
        onChange: t37,
        options: BOOL_OPTIONS
      })
    });
    $[60] = t36;
    $[61] = t37;
    $[62] = t38;
  } else {
    t38 = $[62];
  }
  let t39;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = /*#__PURE__*/_jsx("label", {
      className: "text-sm text-gray-500",
      children: "Anteckningar"
    });
    $[63] = t39;
  } else {
    t39 = $[63];
  }
  let t40;
  if ($[64] !== set) {
    t40 = e_0 => set("additionalNotes", e_0.target.value);
    $[64] = set;
    $[65] = t40;
  } else {
    t40 = $[65];
  }
  let t41;
  if ($[66] !== form.additionalNotes || $[67] !== t40) {
    t41 = /*#__PURE__*/_jsxs("div", {
      children: [t39, /*#__PURE__*/_jsx("textarea", {
        value: form.additionalNotes,
        onChange: t40,
        className: "mt-1 h-20 w-full resize-none rounded border px-3 py-2 text-sm"
      })]
    });
    $[66] = form.additionalNotes;
    $[67] = t40;
    $[68] = t41;
  } else {
    t41 = $[68];
  }
  let t42;
  if ($[69] !== t12 || $[70] !== t16 || $[71] !== t19 || $[72] !== t22 || $[73] !== t23 || $[74] !== t26 || $[75] !== t29 || $[76] !== t32 || $[77] !== t35 || $[78] !== t38 || $[79] !== t4 || $[80] !== t41 || $[81] !== t8) {
    t42 = /*#__PURE__*/_jsxs(_Fragment, {
      children: [t4, t8, t12, t16, t19, t22, t23, t26, t29, t32, t35, t38, t41]
    });
    $[69] = t12;
    $[70] = t16;
    $[71] = t19;
    $[72] = t22;
    $[73] = t23;
    $[74] = t26;
    $[75] = t29;
    $[76] = t32;
    $[77] = t35;
    $[78] = t38;
    $[79] = t4;
    $[80] = t41;
    $[81] = t8;
    $[82] = t42;
  } else {
    t42 = $[82];
  }
  return t42;
}
function _temp8(t0) {
  const [k_2, v_6] = t0;
  return {
    value: k_2,
    label: v_6
  };
}
function _temp7(t0) {
  const [k_1, v_4] = t0;
  return {
    value: k_1,
    label: v_4
  };
}
function _temp6(t0) {
  const [k_0, v_2] = t0;
  return {
    value: k_0,
    label: v_2
  };
}
function _temp5(t0) {
  const [k, v_0] = t0;
  return {
    value: k,
    label: v_0
  };
}
function Field(t0) {
  const $ = _c(5);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = /*#__PURE__*/_jsx("span", {
      className: "text-sm text-gray-600",
      children: label
    });
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center justify-between gap-3",
      children: [t1, children]
    });
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function MiniSelect(t0) {
  const $ = _c(8);
  const {
    value,
    onChange,
    options
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = e => onChange(e.target.value);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== options) {
    t2 = options.map(_temp9);
    $[2] = options;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== value) {
    t3 = /*#__PURE__*/_jsx("select", {
      value: value,
      onChange: t1,
      className: "h-8 rounded border bg-white px-2 text-sm font-medium text-gray-900",
      children: t2
    });
    $[4] = t1;
    $[5] = t2;
    $[6] = value;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp9(o) {
  return /*#__PURE__*/_jsx("option", {
    value: o.value,
    children: o.label
  }, o.value);
}